import { DeviceIsDeviceType, DeviceIsSystemType } from 'features/integrations/utils/deviceComparisionUtils';
import {
  buildAwareDeviceMap,
  buildColorMap,
  buildTimeStampSet,
  getYAxisValue,
  isSelectedAndHasDataWithinTimeRange,
  recordAlertEventAndGetLevel,
} from './deviceGraphHelpers';
import { getUSGSParameterNamesForSelected, isUSGSDevice } from './usgsDeviceGraphUtils';

export const getAwareDevices = (devices) => {
  return devices.filter(
    (device) => DeviceIsSystemType(device, 'Flood') && DeviceIsDeviceType(device, 'Stage'),
  );
};

// Gets the y-axis label (name) based on selected data type and unit type
export const getYAxisLabel = (selectedDataType, unitType) => {
  switch (selectedDataType) {
    case 'sensordepths':
    case 'depths':
      return unitType ? 'Depth (m)' : 'Depth (ft)';
    case 'temp':
      return unitType ? 'Temperature (C)' : 'Temperature (F)';
    case 'baro':
      return unitType ? 'Pressure (mBar)' : 'Pressure (inHg)';
    case 'battery':
      return 'Battery (V)';
    case 'cell signal':
      return 'Cell Signal (dBm)';
    case 'intensity':
      return 'Rain Intensity';
    case 'rainfall':
      return 'Rainfall';
    case 'tips':
      return 'Bucket Tips';
    default:
      return null;
  }
};

// Determines the series type (line chart or bar chart) based on selected data type
export const getSeriesType = (selectedDataType) => {
  if (
    selectedDataType === 'rainfall' ||
    selectedDataType === 'tips' ||
    selectedDataType === 'intensity'
  ) {
    return 'bar';
  }
  return 'line';
};

/**
 * Build logic model for AWARE device chart:
 * - labels: x-axis labels (timestamps)
 * - datasets: logical datasets with meta + color (not tied to any chart lib)
 * - markLineData: [{ xAxis: "timestamp" }, ...] for red-line thresholds
 *
 * @returns {{ labels: string[], datasets: any[], markLineData: {xAxis:string}[] }}
 */

const emptyChart = { labels: [], datasets: [], markLineData: [] };

const buildDatasets = (
  awareDeviceMap,
  selectedDeviceMap,
  filterWithinTimeRange,
  floodAlertEvents,
  selectedDataType,
  colors,
  unitType,
) => {
  return Object.keys(selectedDeviceMap)
    .filter((deviceId) => {
      return isSelectedAndHasDataWithinTimeRange(
        deviceId,
        selectedDeviceMap,
        awareDeviceMap,
        filterWithinTimeRange,
      ); // Skips unselected devices, devices with no data, and devices with no data in time range
    })
    .map((deviceId) => {
      // Creates an echarts series for each device
      const device = awareDeviceMap.get(String(deviceId)); // Gets the aware device that matches the currently selected id
      if (!device) return null;

      const filteredData = device.data
        .filter(filterWithinTimeRange)
        .map((deviceData) => {
          const actualMeasuredWaterDepth = deviceData['sensordepths'];
          const alertLevel = recordAlertEventAndGetLevel(
            actualMeasuredWaterDepth,
            device.device_settings,
            floodAlertEvents,
            deviceData,
          );

          return {
            ...deviceData,
            floodAlertLevel: alertLevel,
          };
        })
        .sort((a, b) => a.ts - b.ts);

      const datapoints = filteredData.map((deviceData) => {
        const { ts: timestamp } = deviceData;
        const yValue = getYAxisValue(deviceData, selectedDataType, unitType);
        return [timestamp, yValue]; // ECharts format: [x, y]
      });
      return {
        _meta: { ...device, data: filteredData }, // Stores the device object from the awareDeviceMap and its filtered data
        label: `${device.static_id} ${device.alias}`, // For the name used in legend and tooltips
        data: datapoints, // ECharts points to plot as [x, y]
        color: colors[device.id],
      };
    })
    .filter(Boolean)
    .reverse();
};

export function buildAwareDeviceChartModel({
  devices = [],
  selectedDeviceMap = {}, // { [alias]: boolean }
  selectedDataType = 'sensordepths',
  unitType = false,
  startTime,
  endTime,
}) {
  if (!devices || devices.length === 0 || !startTime || !endTime) {
    return emptyChart;
  }

  const awareDevices = getAwareDevices(devices);
  const awareDeviceMap = buildAwareDeviceMap(awareDevices);

  if (awareDevices.length === 0) {
    return emptyChart;
  }

  const colors = buildColorMap(awareDevices); // Contains all colors for devices
  const timestampSet = buildTimeStampSet(startTime, endTime); // Contains all timestamps for x-axis labels
  const floodAlertEvents = new Map(); // Stores timestamps where any flood device crossed a threshold (keyed by ts for O(1) lookups)

  // Checks to ensure data point is within the start and end time
  const filterWithinTimeRange = (dataPoint) => {
    const date = new Date(dataPoint.ts); // Converts data point timestamp into a date object
    return date >= startTime && date <= endTime; // Check if data point is within the start and end time
  };

  const datasets = buildDatasets(
    awareDeviceMap,
    selectedDeviceMap,
    filterWithinTimeRange,
    floodAlertEvents,
    selectedDataType,
    colors,
    unitType,
  );

  //TODO: Figure out how to display string labels, but use numeric x-axis values
  const labels = [...timestampSet].map((ts) => new Date(ts).toLocaleDateString());

  if (!datasets.length) {
    return emptyChart;
  }

  // Convert threshold events into ECharts-ready markLineData with colors
  const alertLevelColors = {
    1: '#ffCC00', // Yellow for alert level 1
    2: '#ff7b00ff', // Orange for alert level 2
    3: '#ff0000', // Red for alert level 3
  };

  const markLineData = [...floodAlertEvents.values()].map((event) => ({
    xAxis: event.timestamp,
    level: event.level,
    color: alertLevelColors[event.level],
  }));

  // labels = x-axis labels (timestamps)
  // datasets = points to plot with meta info (colors and etc)
  // markLineData = flood alert level lines (vertical yellow, orange, and red lines on chart)
  return { labels, datasets, markLineData };
}

// Creates a custom tooltip that appears when hovering over data points in the AWARE device chart
export function buildAwareTooltipFormatter(datasets, imageMap = new Map(), unitType = false) {
  return (params) => {
    const items = Array.isArray(params) ? params : [params];
    if (!items.length) return null;

    const ts = items[0].axisValue ?? (Array.isArray(items[0].value) ? items[0].value[0] : null);

    const label = ts ? new Date(ts).toLocaleString() : '';

    const lines = [`<strong>${label}</strong>`];
    let imageForTooltip = null;

    items.forEach((pointParam) => {
      const dataset = datasets[pointParam.seriesIndex];
      const raw = dataset?._meta?.byTimestampMap?.get(ts);

      if (raw) {
        const actualDepth = Number(raw.sensordepths ?? raw.depth1 ?? 0);
        const sensorDepth = Number(raw.depth1 ?? 0);
        const temperature = Number(raw.h2oTemp ?? 0);
        const pressure = Number(raw.baro ?? 0);
        const battery = Number(raw.battery ?? 0);
        const floodAlertLevel = Number(raw.floodAlertLevel ?? 0);

        const alert =
          floodAlertLevel >= 3
            ? 'Threshold 3'
            : floodAlertLevel === 2
              ? 'Threshold 2'
              : floodAlertLevel === 1
                ? 'Threshold 1'
                : 'None';

        const actualDepthFeet = actualDepth / 12;
        const sensorDepthFeet = sensorDepth / 12;
        const actualDepthMeters = actualDepth * 0.0254;
        const sensorDepthMeters = sensorDepth * 0.0254;

        const depthLines = unitType
          ? [
              `Actual Depth (m): ${actualDepthMeters.toFixed(2)} m`,
              `Sensor Depth (m): ${sensorDepthMeters.toFixed(2)} m`,
            ]
          : [
              `Actual Depth (in/ft): ${actualDepth.toFixed(
                2,
              )} in / ${actualDepthFeet.toFixed(2)} ft`,
              `Sensor Depth (in/ft): ${sensorDepth.toFixed(
                2,
              )} in / ${sensorDepthFeet.toFixed(2)} ft`,
            ];

        lines.push(
          `<span>${pointParam.marker}${pointParam.seriesName}</span>`,
          ...depthLines,
          `Temperature (F/C): ${((temperature * 9) / 5 + 32).toFixed(
            2,
          )} F / ${temperature.toFixed(2)} C`,
          `Pressure (mBar/inHg): ${pressure.toFixed(2)} mBar / ${(pressure / 33.864).toFixed(
            2,
          )} inHg`,
          `Battery (V): ${battery}V`,
          `Alert: ${alert}`,
        );

        if (!imageForTooltip && raw?.img_link_url) {
          imageForTooltip =
            (imageMap instanceof Map
              ? imageMap.get(raw.img_link_url)
              : imageMap?.[raw.img_link_url]) || raw.img_link_url;
        }
      } else {
        const y = Array.isArray(pointParam.value) ? pointParam.value[1] : pointParam.value;
        lines.push(`${pointParam.marker}${pointParam.seriesName}: ${y}`);
      }
    });

    if (imageForTooltip) {
      lines.push(
        `<div style="margin-top:8px;"><img src="${imageForTooltip}" alt="Aware Camera Capture" style="max-width:240px; max-height:160px; display:block;" /></div>`,
      );
    }

    return `<div class="echart-tooltip-content">${lines.join('<br/>')}</div>`;
  };
}

// Check if any selected AWARE device has a tipping bucket
export const hasTippingBucket = (devices, selectedDeviceMap) => {
  const awareDevices = getAwareDevices(devices);

  return Object.keys(selectedDeviceMap).some((deviceId) => {
    const isDeviceSelected = selectedDeviceMap[deviceId] === true;
    if (!isDeviceSelected) return false; // Skip unselected devices

    const matchingDevice = awareDevices.find((device) => String(device.id) === String(deviceId));
    if (!matchingDevice || matchingDevice.data.length === 0) return false; // Skip if no matching AWARE device or no data

    return matchingDevice.device_settings?.externalDevice === 1;
  });
};

export const getFilterDropdownOptions = (devices, selectedDeviceMap, unitType = false) => {
  const awareBase = [
    <option value="sensordepths">Actual Depths</option>,
    <option value="depths">Sensor Depths</option>,
    <option value="temp">Temperature</option>,
    <option value="baro">Barometric Pressure</option>,
    <option value="battery">Battery</option>,
    <option value="cell signal">Cell Signal</option>,
  ];

  // Find the first selected alias and device
  const firstSelectedId = selectedDeviceMap
    ? Object.keys(selectedDeviceMap).find((k) => selectedDeviceMap[k])
    : null;
  const firstDevice = firstSelectedId
    ? devices.find((d) => String(d.id) === String(firstSelectedId))
    : null;

  // If the first selected device is AWARE -> return AWARE options
  if (
    firstDevice &&
    DeviceIsSystemType(firstDevice, 'Flood') &&
    DeviceIsDeviceType(firstDevice, 'Stage')
  ) {
    const awareOptions = [...awareBase];
    if (hasTippingBucket(devices, selectedDeviceMap)) {
      awareOptions.push(<option value="intensity">Rain Intensity</option>);
      awareOptions.push(<option value="rainfall">Rainfall</option>);
      awareOptions.push(<option value="tips">Bucket Tips</option>);
    }
    return awareOptions;
  }

  // Else if the first selected device is USGS -> return USGS parameters (or fallback Gage Height)
  else if (firstDevice && isUSGSDevice(firstDevice)) {
    try {
      const params = getUSGSParameterNamesForSelected(devices, selectedDeviceMap, unitType) || [];
      if (params.length > 0) {
        return params.map((parameter) => {
          const name = parameter && typeof parameter === 'object' ? parameter.name : parameter;
          const unit = parameter && typeof parameter === 'object' ? parameter.unit : null;
          const label = unit ? `${name} (${unit})` : name;
          return (
            <option key={`usgs-param-${name}`} value={name}>
              {label}
            </option>
          );
        });
      }
    } catch (e) {
      // noop
    }
    return [<option value="Gage Height">Gage Height</option>];
  }

  // Default: return AWARE base options
  return awareBase;
};

export const getDeviceDropdownOptions = (devices) => {
  return getAwareDevices(devices).map((device) => device.alias);
};

/**
 * Given a group ID, the full groups list, and the set of flood-capable device IDs
 * (AWARE + USGS), returns a selectedDeviceMap for the graph containing only the
 * applicable devices in the group, or null if none qualify.
 *
 * @param {number|null} linkingGroupId
 * @param {object[]} allGroups
 * @param {string[]} floodDeviceIds - string IDs of all flood-capable devices
 * @returns {Record<string, true> | null}
 */
export const resolveGroupDeviceMap = (linkingGroupId, allGroups, floodDeviceIds) => {
  if (linkingGroupId == null) return null;
  const group = allGroups.find((g) => g.id === linkingGroupId);
  if (!group) return null;
  const groupDeviceIds = (group.devices || []).map(String);
  const applicableIds = groupDeviceIds.filter((id) => floodDeviceIds.includes(id));
  if (applicableIds.length === 0) return null;
  const map = {};
  applicableIds.forEach((id) => {
    map[id] = true;
  });
  return map;
};
